import type { FC } from 'react'
import type { ArtworkGridMode, ArtworkGridProps } from './ArtworkGrid'
import ArtworkGrid from './ArtworkGrid'
import type { BaseArtworkInfiniteScrollProps } from './BaseArtworkInfiniteScroll'
import BaseArtworkInfiniteScroll from './BaseArtworkInfiniteScroll'
import type { ArtworkFeedResult } from './useArtworkFeed'

export type StatefulArtworkGridProps =
  // Pass-through to BaseArtworkInfiniteScroll for empty-state copy + tracking
  Pick<
    BaseArtworkInfiniteScrollProps,
    | 'q'
    | 'order'
    | 'pageSize'
    | 'trackingArgs'
    | 'scrollContainer'
    | 'showDislikeOverlay'
    | 'renderEmpty'
    | 'containerProps'
    | 'showSpeedDial'
    | 'onInitialLoad'
  > & {
    feedResult: ArtworkFeedResult
    // ArtworkGrid-specific
    mode?: ArtworkGridMode
    enableTracking?: boolean
    showDislike?: boolean
    modifyArtworkElements?: ArtworkGridProps['modifyArtworkElements']
    artworkCardProps?: ArtworkGridProps['artworkCardProps']
  }

const StatefulArtworkGrid: FC<StatefulArtworkGridProps> = ({
  // Feed data
  feedResult,

  // Presentation pass-through
  q,
  order,
  pageSize,
  trackingArgs,
  scrollContainer,
  showDislikeOverlay,
  renderEmpty,
  containerProps,
  showSpeedDial,
  onInitialLoad,

  // ArtworkGrid-specific
  enableTracking,
  showDislike,
  modifyArtworkElements,
  artworkCardProps,
  mode,
}) => {
  // Previously wrapped in `<Suspense>` so any suspending hook in the
  // tree (e.g. `useTranslation([ns])` with `useSuspense: true`) would
  // be caught locally instead of bubbling up to the route shell. With
  // `useSuspense: false` (set in `src/i18n/i18next.ts`) and no
  // `useSuspenseQuery` / `React.lazy` / `dynamic({ ssr: false })`
  // anywhere in the rendered subtree, nothing throws on the server
  // any more — and the boundary itself was the root cause of `/zh`
  // shipping a hidden `<div id="S:0">` swap target that JS-disabled
  // crawlers and NoScript users see as a blank artwork grid. React 19's
  // streaming Suspense protocol emits placeholder + hidden content
  // + `$RC` swap script for any pending boundary, regardless of
  // whether a fallback is set; without JS to run the swap the grid
  // stays empty. Removing the boundary lets the rendered grid HTML
  // (loading skeleton on first SSR, real cards after Apollo cache
  // hits) inline directly into `<main>`.
  return (
    <BaseArtworkInfiniteScroll
      feedResult={feedResult}
      q={q}
      order={order}
      pageSize={pageSize}
      trackingArgs={trackingArgs}
      scrollContainer={scrollContainer}
      showDislikeOverlay={showDislikeOverlay}
      renderEmpty={renderEmpty}
      containerProps={containerProps}
      showSpeedDial={showSpeedDial}
      onInitialLoad={onInitialLoad}
      renderContent={innerProps => (
        <ArtworkGrid
          key={'artwork-grid-' + mode}
          enableTracking={enableTracking}
          showDislike={showDislike}
          showDislikeOverlay={showDislikeOverlay}
          modifyArtworkElements={modifyArtworkElements}
          artworkCardProps={artworkCardProps}
          mode={mode}
          {...innerProps}
        />
      )}
      renderLoading={() => <ArtworkGrid loading mode={mode} />}
    />
  )
}

export default StatefulArtworkGrid
